import format from 'date-fns/format';
import { cloneDeep } from 'lodash/fp';
import React from 'react';
import { useIntl } from 'react-intl';

import { TCallbackForActionGetPickUPTimesForCart } from '@/entities/cart/model/types';

import { ModalOrderAhead } from '@/shared/components/ModalOrderAhead';
import {
  FORMAT_DATE_TIME,
  FULFILLMENT_METHODS,
  Fulfillment,
  FulfillmentType,
  MODE,
} from '@/shared/constants';
import { useAppDispatch } from '@/shared/hooks/appRedux';
import { ModalService } from '@/shared/services/modal';
import type { ICartBasket } from '@/shared/types/cart';
import { noop } from '@/shared/utils/noop';

import { GetPickUPTimesForCart, UpdateLocationAndTimeForOrdering } from '../../../index';
import {
  ButtonChangePickUp,
  PickUPOrderComponent,
  RowPickUp,
  SpanPickUp,
  SpanPickUpWall,
  WrapperPickUpLocationComponent,
} from '../parts';

interface IProps {
  basket: ICartBasket;
  orderType: string;
  orderTypes: {
    Service: string;
    Merchandise: string;
    Food: string;
  };
  orderNumber: string;
}

const PickUpOrder: React.FC<IProps> = ({ basket, orderType, orderTypes, orderNumber }) => {
  const dispatch = useAppDispatch<CartAppDispatch>();

  const { formatMessage } = useIntl();

  const lastIndex = 1;
  const { MMDDYYYY } = FORMAT_DATE_TIME;
  const date = format(new Date(basket.OrderDateToString), MMDDYYYY);

  const messages = {
    pickUpAt: formatMessage({ id: 'PickUpAt' }),
    pickUp: formatMessage({ id: 'PickUp' }),
    delivery: formatMessage({ id: 'DeliveryAt' }),
    deliveryTo: formatMessage({ id: 'DeliveryTo' }),
    shipping: formatMessage({ id: 'Shipping' }),
    at: formatMessage({ id: 'Checkout.Cart.Labels.At' }),
    change: formatMessage({ id: 'Change' }),
    changeAriaLabel: formatMessage(
      { id: 'Checkout.Cart.Buttons.PickUp.AriaLabel' },
      { 0: orderNumber },
    ),
    buttonOrderAhead: formatMessage({ id: 'Checkout.Cart.Modals.OrderAhead.Buttons.Update' }),
  };

  const getLabel = (
    IsPickupFulfillmentMethod: boolean,
    IsDeliveryFulfillmentMethod: boolean,
    IsShippingFulfillmentMethod: boolean,
  ): string => {
    const isPickupOrDeliveryLocation =
      basket?.PickupOrDeliveryLocation?.replace(/\s+$/g, '').length > 0;

    if (IsPickupFulfillmentMethod && !IsDeliveryFulfillmentMethod && !IsShippingFulfillmentMethod) {
      const label = isPickupOrDeliveryLocation
        ? `${messages.pickUpAt} ${basket?.PickupOrDeliveryLocation}`
        : messages.pickUp;

      return label;
    }

    if (!IsPickupFulfillmentMethod && IsDeliveryFulfillmentMethod && !IsShippingFulfillmentMethod) {
      const label = isPickupOrDeliveryLocation
        ? `${messages.deliveryTo} ${basket?.PickupOrDeliveryLocation}`
        : messages.delivery;

      return label;
    }

    if (!IsPickupFulfillmentMethod && !IsDeliveryFulfillmentMethod && IsShippingFulfillmentMethod) {
      const isShippingAddress = basket?.ShippingAddress!.replace(/\s+$/g, '').length > 0;
      const label = isShippingAddress
        ? `${messages.shipping}: ${basket?.ShippingAddress}`
        : messages.shipping;
      return label;
    }

    return '';
  };

  const getObjectFulFilment = (
    IsPickup: boolean,
    FulfillmentDate: string,
    FulfillmentTime: string,
    FulfillmentInstructions: string,
    DeliveryLocation: string,
    PickUpArea: string,
    FulfillmentDateTime: string,
  ): FulfillmentType => {
    const fulFilment = cloneDeep(Fulfillment);
    fulFilment.PickUpLocation = PickUpArea;
    fulFilment.SelectedDate = FulfillmentDateTime;
    if (IsPickup) {
      fulFilment.PickUpFulfillment.PickUpTime = FulfillmentTime;
      fulFilment.PickUpFulfillment.PickUpDate = FulfillmentDate;
      fulFilment.PickUpFulfillment.RecipientInstructions = FulfillmentInstructions;
      return fulFilment;
    }
    fulFilment.DeliveryLocation = DeliveryLocation;
    fulFilment.DeliveryFulfillment.DeliveryDate = FulfillmentDate;
    fulFilment.DeliveryFulfillment.DeliveryTime = FulfillmentTime;
    fulFilment.DeliveryFulfillment.DeliveryLocation = DeliveryLocation;
    fulFilment.DeliveryFulfillment.RecipientInstructions = FulfillmentInstructions;
    fulFilment.SelectedFulfillmentMethod = FULFILLMENT_METHODS.DELIVERY.name;
    return fulFilment;
  };

  const handleOpenModal = (
    pickupTimeIncrement: number,
    locationId: string,
    storeIds: Nullable<string>,
    periodId: Nullable<number>,
    IsPickup: boolean,
    IsDelivery: boolean,
    OnlyShowCurrentDaysOnlineOrdering: boolean,
    OnlyAllowFutureDaysOnlineOrdering: boolean,
    FulfillmentDate: string,
    FulfillmentTime: string,
    FulfillmentInstructions: string,
    DeliveryLocation: string,
    PickUpArea: string,
    FulfillmentDateTime: string,
  ): void => {
    const fulfillment = getObjectFulFilment(
      IsPickup,
      FulfillmentDate,
      FulfillmentTime,
      FulfillmentInstructions,
      DeliveryLocation,
      PickUpArea,
      FulfillmentDateTime,
    );

    const callback: TCallbackForActionGetPickUPTimesForCart = (defaultPickUPTimes, menuCalendar) =>
      ModalService.open(ModalOrderAhead, {
        basketName: basket.Name,
        locationId,
        storeIds,
        pickupTimeIncrement,
        nameButton: messages.buttonOrderAhead,
        preselectedLocation: basket.PickupOrDeliveryLocation,
        preselectedTime: basket.OrderDateToString,
        isMealSelectDisabled: true,

        tabIndexParent: lastIndex,
        OnlyShowCurrentDaysOnlineOrdering,
        OnlyAllowFutureDaysOnlineOrdering,
        isPickUpTab: IsPickup,
        isDeliveryTab: IsDelivery,
        defaultPickUPTimes,
        fulfillment,
        // В корзине нет надобности сохранять Fulfillment (он берется напрямую из баскета)
        setFulfillment: noop,
        orderMenuDate: basket.OrderDateToString,
        // В корзине нет надобности сохранять OrderMenuDate (он берется напрямую из баскета)
        setOrderMenuDate: noop,

        mode: MODE.CART,
        menuCalendar,
        selectedTabIndex: basket.BasketFulfillmentMethod,

        handlePickUpTimes: (payload) =>
          dispatch(GetPickUPTimesForCart({ ...payload, isModal: true })),
        handleFetchMenu: (payload) =>
          dispatch(
            UpdateLocationAndTimeForOrdering({
              basket: payload.basket,
              fulfillmentMethod: payload.fulfillmentMethod,
              pickupLocation: payload.pickupLocation,
              selectedDate: payload.customDate,
              updatedDateTime: payload.updatedDateTime,
              callback: payload.callback,
            }),
          ),
      });
    dispatch(
      GetPickUPTimesForCart({
        pickupTimeIncrement,
        date,
        locationId,
        storeIds,
        periodId,
        basketName: basket.Name,
        isModal: false,
        callback,
      }),
    );
  };
  const isMealPlan: boolean = orderType === orderTypes.Service;
  const isMerch: boolean = orderType === orderTypes.Merchandise;

  const labelName: string = getLabel(
    basket?.IsPickupFulfillmentMethod,
    basket?.IsDeliveryFulfillmentMethod,
    basket?.IsShippingFulfillmentMethod,
  );

  const isDisplayingDate = !!basket?.OrderDateToString || !!basket.OrderTime;

  const pickupDate = new Date(basket?.OrderDateToString);

  const dataLabel = `${format(pickupDate, FORMAT_DATE_TIME.MMdd)}`;

  const dateTimeLabel = basket?.OrderTime
    ? `${basket?.OrderDateToString && dataLabel ? ` ${messages.at} ` : ''}${basket?.OrderTime}`
    : '';
  const fullDataTimeLabel = (basket?.OrderDateToString ? dataLabel : '') + dateTimeLabel;
  const isFullDataTimeLabel = fullDataTimeLabel.replace(/\s+$/g, '').length > 0;

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {!isMealPlan && (
        <PickUPOrderComponent className="PickUPOrderComponent">
          <WrapperPickUpLocationComponent className="WrapperPickUpLocationComponent">
            <RowPickUp className="RowPickUp">
              <SpanPickUp
                className="SpanPickUp"
                dangerouslySetInnerHTML={{
                  __html: labelName,
                }}
              />
              {isFullDataTimeLabel && isDisplayingDate && fullDataTimeLabel.trim() !== '' && (
                <>
                  <SpanPickUpWall className="SpanPickUpWall">|</SpanPickUpWall>
                  <SpanPickUp
                    className="SpanPickUp"
                    dangerouslySetInnerHTML={{
                      __html: fullDataTimeLabel,
                    }}
                  />
                </>
              )}
            </RowPickUp>
            {!isMerch && (
              <ButtonChangePickUp
                className="ButtonChangePickUp"
                aria-label={messages.changeAriaLabel}
                aria-disabled="false"
                role="button"
                onClick={() =>
                  handleOpenModal(
                    basket.Location.PickupTimeIncrement,
                    basket.LocationId,
                    basket.StoreId,
                    basket.PeriodId,
                    basket.IsPickupFulfillmentMethod,
                    basket.IsDeliveryFulfillmentMethod,
                    basket.OnlyShowCurrentDaysOnlineOrdering,
                    basket.OnlyAllowFutureDaysOnlineOrdering,
                    basket.OrderDateToString,
                    basket.OrderTime,
                    '',
                    basket.PickupOrDeliveryLocation,
                    '',
                    '',
                  )
                }
                dangerouslySetInnerHTML={{
                  __html: messages.change,
                }}
              />
            )}
          </WrapperPickUpLocationComponent>
        </PickUPOrderComponent>
      )}
    </>
  );
};
export default PickUpOrder;
